/**
 * Row classification for grouped imports — one spreadsheet row becomes one
 * document line, and rows sharing a `source-ref` become one document.
 *
 * Validation here is a UX affordance: the user sees what will be skipped
 * before pressing Import. The server re-validates everything on submit.
 */
import type { GroupedDocument, ImportLine, PreviewRow } from "./types.ts";

/**
 * Header fields every grouped document shares. Document-specific dates
 * (`dueOn` for invoices, `expiresOn` for proformas) are spread on top by
 * the caller's `buildHeader`.
 *
 * INR is assumed: foreign currency and non-standard tax treatments go
 * through the composer, not import.
 */
export function commonHeaderFields(raw: Record<string, string>) {
  return {
    customerId: (raw["customer-id"] ?? "").trim(),
    issuedOn: (raw["issued-on"] ?? "").trim(),
    placeOfSupply: (raw["place-of-supply"] ?? "").trim(),
    currencyCode: "INR",
    ...(raw["customer-note"]?.trim() ? { customerNote: raw["customer-note"].trim() } : {}),
    ...(raw["internal-note"]?.trim() ? { internalNote: raw["internal-note"].trim() } : {}),
  };
}

interface ClassifyOptions<THeader> {
  /** Singular document noun, used only in the missing-source-ref message. */
  noun: string;
  /** Indefinite article for `noun` — "an" for invoice, "a" for proforma. */
  article: string;
  /** Builds the document-level header fields from a row. */
  buildHeader: (raw: Record<string, string>) => THeader;
}

/** Turns one mapped row into a preview row, parsing and validating it. */
export function classifyGroupedRow<THeader>(
  raw: Record<string, string>,
  index: number,
  { noun, article, buildHeader }: ClassifyOptions<THeader>,
): PreviewRow<THeader> {
  const sourceRef = (raw["source-ref"] ?? "").trim();
  if (!sourceRef) {
    return {
      index,
      raw,
      parsed: null,
      status: "validation",
      message: `Missing source-ref (this row can't be grouped into ${article} ${noun}).`,
    };
  }

  const invalid = (message: string): PreviewRow<THeader> => ({
    index,
    raw,
    parsed: { sourceRef, line: null, header: null },
    status: "validation",
    message,
  });

  const description = (raw.description ?? "").trim();
  const hsn = (raw.hsn ?? "").trim();
  const qty = Number((raw.quantity ?? "").trim());
  const rate = Number((raw["rate-rupees"] ?? "").trim());
  const tax = Number((raw["tax-percent"] ?? "").trim());
  const discount = Number((raw["discount-percent"] ?? "0").trim() || "0");

  if (!description) return invalid("Missing description.");
  if (hsn.length < 4 || hsn.length > 8) return invalid("HSN must be 4-8 characters.");
  if (!Number.isFinite(qty) || qty <= 0) return invalid("Quantity must be a positive number.");
  if (!Number.isFinite(rate) || rate < 0) {
    return invalid("Rate (rupees) must be a non-negative number.");
  }
  if (!Number.isFinite(tax) || tax < 0 || tax > 100) {
    return invalid("Tax percent must be 0-100.");
  }

  const line: ImportLine = {
    description,
    hsn,
    unit: (raw.unit ?? "Nos").trim() || "Nos",
    qtyMilli: Math.round(qty * 1000),
    ratePaise: String(Math.round(rate * 100)),
    discountBps: Math.round(discount * 100),
    taxRateBps: Math.round(tax * 100),
  };

  return {
    index,
    raw,
    parsed: { sourceRef, line, header: buildHeader(raw) },
    status: "valid",
    message: "Ready to import.",
  };
}

/** Groups valid preview rows by `source-ref`; the first row wins the header. */
export function groupBySourceRef<THeader>(
  rows: ReadonlyArray<PreviewRow<THeader>>,
): Array<GroupedDocument<THeader>> {
  const map = new Map<string, GroupedDocument<THeader>>();
  for (const r of rows) {
    if (r.status !== "valid" || !r.parsed?.line || !r.parsed.header) continue;
    const existing = map.get(r.parsed.sourceRef);
    if (existing) {
      existing.lines.push(r.parsed.line);
      existing.rowIndices.push(r.index);
    } else {
      map.set(r.parsed.sourceRef, {
        sourceRef: r.parsed.sourceRef,
        header: r.parsed.header,
        lines: [r.parsed.line],
        rowIndices: [r.index],
      });
    }
  }
  return Array.from(map.values());
}
